// Broker staff sign-in (SM-722; own identity since SM-968): email +
// password, then a TOTP challenge when the account has an authenticator
// (428). Under FF_BROKER_STAFF_IDENTITY=staff it is the staff account on
// broker-service, with "Forgot password?"; under client, the trading-site
// session as before (lib/auth.ts picks). Whether the person is broker
// STAFF is decided after sign-in by GET /api/broker/me, so a non-staff
// account is told so by the shell rather than failing here.
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { APIError } from "@/lib/api";
import { login, completeMFA, fetchMeOrNull, forgotPassword, identityMode } from "@/lib/auth";
import { keys } from "@/lib/queries";
import type { SessionEndReason } from "@/lib/session";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { SparkHorizontal } from "@/components/ui/SparkHorizontal";
import { LanguageSelector } from "@/components/LanguageSelector";
import { useI18n } from "@/i18n/I18nProvider";
import type { TranslationKey } from "@/i18n/en";
import { takeChallenge, useFinishSignIn } from "@/components/auth/signIn";
import type { LoginFactorOption, LoginMFAChallenge, LoginResponse } from "@/lib/types";

interface LoginSearch {
  redirect?: string;
  reason?: SessionEndReason;
  /** Open on "Forgot password?" (an expired reset link sends people here). */
  forgot?: boolean;
}

const REASONS: readonly SessionEndReason[] = ["expired", "signed-out", "revoked"];

/** Only same-origin, in-app paths are honoured as a post-login redirect. */
function safeRedirect(target: string | undefined): string {
  if (!target || !target.startsWith("/") || target.startsWith("//") || target.startsWith("/login")) return "/";
  return target;
}

export const Route = createFileRoute("/login")({
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
    reason: REASONS.includes(search.reason as SessionEndReason) ? (search.reason as SessionEndReason) : undefined,
    forgot: search.forgot === true || search.forgot === "true" ? true : undefined,
  }),
  beforeLoad: async ({ context, search }) => {
    const me = await context.queryClient.ensureQueryData({
      queryKey: keys.me(),
      queryFn: fetchMeOrNull,
      staleTime: 5_000,
    });
    if (me && !search.reason) throw redirect({ href: safeRedirect(search.redirect) });
  },
  component: LoginPage,
});

type Step =
  | { kind: "password" }
  | { kind: "mfa"; challenge: LoginMFAChallenge; factor: LoginFactorOption }
  | { kind: "forgot"; sent: boolean };

/** The step a set / reset link handed over (its TOTP challenge), else the password. */
function initialStep(forgot: boolean | undefined): Step {
  const c = takeChallenge();
  const factor = c?.factors?.[0];
  if (c && factor) return { kind: "mfa", challenge: c, factor };
  return forgot ? { kind: "forgot", sent: false } : { kind: "password" };
}

function humanError(e: unknown, step: "password" | "mfa", t: (key: TranslationKey) => string): string {
  if (e instanceof APIError) {
    if (e.status === 401) return step === "mfa" ? t("login.error.codeRejected") : t("login.error.credentials");
    if (e.status === 429) return t("login.error.rateLimit");
    if (e.status === 0 || e.status >= 500) return t("login.error.unreachable");
    if (e.message && !/^HTTP \d+$/.test(e.message)) return e.message;
  }
  return step === "mfa" ? t("login.error.verify") : t("login.error.signIn");
}

function LoginPage() {
  const { t } = useI18n();
  const search = Route.useSearch();
  const finish = useFinishSignIn();
  const mode = useQuery({ queryKey: ["identity-mode"], queryFn: identityMode, staleTime: Infinity });
  const staffSignIn = mode.data === "staff";
  const [step, setStep] = useState<Step>(() => initialStep(search.forgot));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState<string[]>(["", "", "", "", "", ""]);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (step.kind === "mfa") otpRefs.current[0]?.focus();
  }, [step.kind]);

  const goAfterAuth = (res: LoginResponse) => finish(res, safeRedirect(search.redirect));

  const forgotMut = useMutation({
    mutationFn: () => forgotPassword(email.trim()),
    // The same answer whether or not the email has a staff account.
    onSuccess: () => {
      setErr(null);
      setStep({ kind: "forgot", sent: true });
    },
    onError: (e) => setErr(humanError(e, "password", t)),
  });

  const passwordMut = useMutation({
    mutationFn: () => login({ email, password }),
    onSuccess: (res) => {
      setErr(null);
      if (res.kind === "needs_mfa") {
        const factor = res.challenge.factors?.[0];
        if (!factor) {
          setErr(t("login.error.noFactor"));
          return;
        }
        setStep({ kind: "mfa", challenge: res.challenge, factor });
        return;
      }
      goAfterAuth(res.data);
    },
    onError: (e) => setErr(humanError(e, "password", t)),
  });

  const mfaMut = useMutation({
    mutationFn: () => {
      if (step.kind !== "mfa") throw new Error("not in MFA step");
      return completeMFA({
        challenge_id: step.challenge.challenge_id,
        factor_id: step.factor.factor_id,
        code: otp.join(""),
      });
    },
    onSuccess: (res) => {
      setErr(null);
      goAfterAuth(res);
    },
    onError: (e) => setErr(humanError(e, "mfa", t)),
  });

  function setOtpAt(i: number, value: string) {
    const digits = value.replace(/\D/g, "");
    const next = otp.slice();
    if (!digits) {
      next[i] = "";
      setOtp(next);
      return;
    }
    let cursor = i;
    for (const d of digits) {
      if (cursor > 5) break;
      next[cursor] = d;
      cursor++;
    }
    setOtp(next);
    otpRefs.current[Math.min(cursor, 5)]?.focus();
    if (cursor >= 6 && next.every((d) => d !== "")) mfaMut.mutate();
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-wrap items-center gap-3">
          <SparkHorizontal viewBox="243 632 1611 628" className="h-8 w-auto shrink-0 text-t-1" />
          <div className="mr-auto text-2xs uppercase tracking-[0.18em] text-t-3">{t("login.brand")}</div>
          <LanguageSelector compact />
        </div>
        <main className="modal">
          <div className="modal-glow" />
          <div className="modal-body">
            {search.reason && (
              <div className="banner warn" role="status">
                <span aria-hidden>ℹ</span>
                <span>{t(`login.reason.${search.reason}`)}</span>
              </div>
            )}
            {step.kind === "forgot" ? (
              <>
                <div className="auth-eyebrow">{t("login.eyebrow")}</div>
                <h1 className="auth-headline">{t("forgot.title")}</h1>
                {step.sent ? (
                  <div className="banner" role="status">
                    <span aria-hidden>✉</span>
                    <span>{t("forgot.sent")}</span>
                  </div>
                ) : (
                  <p className="auth-sub">{t("forgot.subtitle")}</p>
                )}
                {err && (
                  <div className="banner danger" role="alert">
                    <span aria-hidden>⚠</span>
                    <span>{err}</span>
                  </div>
                )}
                {!step.sent && (
                  <form
                    className="space-y-4"
                    onSubmit={(e) => {
                      e.preventDefault();
                      forgotMut.mutate();
                    }}
                  >
                    <Input
                      label={t("login.email")}
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="email"
                      autoFocus
                      required
                    />
                    <Button type="submit" loading={forgotMut.isPending} className="btn-block btn-lg mt-1">
                      {t("forgot.submit")}
                    </Button>
                  </form>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setStep({ kind: "password" });
                    setErr(null);
                  }}
                  className="mt-3 block w-full text-center text-2xs text-t-3 transition-colors hover:text-t-1"
                >
                  {t("login.back")}
                </button>
              </>
            ) : step.kind === "password" ? (
              <>
                <div className="auth-eyebrow">{t("login.eyebrow")}</div>
                <h1 className="auth-headline">{t("login.signIn")}</h1>
                <p className="auth-sub">{t("login.subtitle")}</p>
                {err && (
                  <div className="banner danger" role="alert">
                    <span aria-hidden>⚠</span>
                    <span>{err}</span>
                  </div>
                )}
                <form
                  className="space-y-4"
                  onSubmit={(e) => {
                    e.preventDefault();
                    passwordMut.mutate();
                  }}
                >
                  <Input
                    label={t("login.email")}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    autoFocus
                    required
                  />
                  <Input
                    label={t("login.password")}
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                  />
                  <Button type="submit" loading={passwordMut.isPending} className="btn-block btn-lg mt-1">
                    {passwordMut.isPending ? t("login.signingIn") : t("login.continue")}
                  </Button>
                </form>
                {staffSignIn && (
                  <button
                    type="button"
                    onClick={() => {
                      setStep({ kind: "forgot", sent: false });
                      setErr(null);
                    }}
                    className="mt-3 block w-full text-center text-xs text-t-2 transition-colors hover:text-t-1"
                  >
                    {t("login.forgot")}
                  </button>
                )}
              </>
            ) : (
              <>
                <div className="auth-eyebrow">{t("login.twoFactor")}</div>
                <h1 className="auth-headline">{t("login.enterCode")}</h1>
                <p className="auth-sub">{t("login.codeInstruction", { email: step.challenge.email })}</p>
                <form
                  className="space-y-3"
                  onSubmit={(e) => {
                    e.preventDefault();
                    mfaMut.mutate();
                  }}
                >
                  <div className="otp" role="group" aria-label={t("login.enterCode")}>
                    {otp.map((v, i) => (
                      <input
                        key={i}
                        ref={(el) => {
                          otpRefs.current[i] = el;
                        }}
                        maxLength={6}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={v}
                        onChange={(e) => setOtpAt(i, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Backspace" && !v && i > 0) otpRefs.current[i - 1]?.focus();
                        }}
                        autoComplete="one-time-code"
                        className={v ? "filled" : undefined}
                        aria-label={t("login.digitAria", { digit: i + 1 })}
                      />
                    ))}
                  </div>
                  {err && (
                    <div className="banner danger" role="alert">
                      <span aria-hidden>⚠</span>
                      <span>{err}</span>
                    </div>
                  )}
                  <Button type="submit" loading={mfaMut.isPending} disabled={otp.some((d) => !d)} className="btn-block btn-lg">
                    {mfaMut.isPending ? t("login.verifying") : t("login.verify")}
                  </Button>
                  <button
                    type="button"
                    onClick={() => {
                      setStep({ kind: "password" });
                      setOtp(["", "", "", "", "", ""]);
                      setErr(null);
                    }}
                    className="block w-full text-center text-2xs text-t-3 transition-colors hover:text-t-1"
                  >
                    {t("login.back")}
                  </button>
                </form>
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
